import { useState } from 'react';
import { Connection } from '../../services/connectionService';
import { aiService } from '../../services/aiService';
import { useConfirm } from '../../hooks/useConfirm';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

interface AIConnectionProps {
  connection?: Connection;
  onUpdate: () => void;
}

type AIProvider = 'openai' | 'azure' | 'anthropic';

const OPENAI_MODELS = [
  'gpt-4o',
  'gpt-4o-mini',
  'gpt-4-turbo',
  'gpt-4',
  'gpt-3.5-turbo',
];

// Per Anthropic's current model catalog. `claude-opus-4-7` is the recommended
// default for new code; cheaper tiers are exposed so cost-sensitive users can
// drop down without losing the integration.
const ANTHROPIC_MODELS = [
  'claude-opus-4-7',
  'claude-opus-4-6',
  'claude-sonnet-4-6',
  'claude-haiku-4-5',
];

function defaultModelFor(provider: AIProvider): string {
  if (provider === 'anthropic') return 'claude-opus-4-7';
  return 'gpt-4o-mini';
}

export function AIConnection({ connection, onUpdate }: AIConnectionProps) {
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [testing, setTesting] = useState(false);
  const [provider, setProvider] = useState<AIProvider>(
    connection?.config?.provider || 'openai'
  );
  const [apiKey, setApiKey] = useState('');
  const [azureEndpoint, setAzureEndpoint] = useState(
    connection?.config?.azure_endpoint || ''
  );
  const [azureDeployment, setAzureDeployment] = useState(
    connection?.config?.deployment_name || ''
  );
  const [model, setModel] = useState(
    connection?.config?.model || 'gpt-4o-mini'
  );
  const { confirm, ConfirmDialog } = useConfirm();

  const handleConfigure = () => {
    setIsConfiguring(true);
  };

  const handleCancel = () => {
    setIsConfiguring(false);
    setApiKey('');
    setAzureEndpoint('');
    setAzureDeployment('');
    const prov = connection?.config?.provider || 'openai';
    setModel(connection?.config?.model || defaultModelFor(prov));
    setProvider(prov);
  };

  // Swap the displayed default model when the user switches providers so they
  // see a sensible value before opening the dropdown.
  const handleProviderChange = (next: AIProvider) => {
    setProvider(next);
    if (next !== connection?.config?.provider) {
      setModel(defaultModelFor(next));
    }
  };

  const handleSave = async () => {
    if (!apiKey.trim()) {
      toast.error('API key is required');
      return;
    }

    if (provider === 'azure') {
      if (!azureEndpoint.trim()) {
        toast.error('Azure endpoint is required');
        return;
      }
      if (!azureDeployment.trim()) {
        toast.error('Azure deployment name is required');
        return;
      }
    }

    // Clean Azure endpoint - extract just the base URL
    let cleanedEndpoint = azureEndpoint;
    if (provider === 'azure' && azureEndpoint) {
      try {
        const url = new URL(azureEndpoint);
        // Keep only protocol and host (e.g., https://resource.cognitiveservices.azure.com)
        cleanedEndpoint = `${url.protocol}//${url.host}`;
      } catch (e) {
        toast.error('Invalid Azure endpoint URL');
        return;
      }
    }

    const toastId = toast.loading('Saving AI configuration...');

    try {
      const configPayload = {
        provider,
        api_key: apiKey,
        azure_endpoint: provider === 'azure' ? cleanedEndpoint : undefined,
        azure_deployment: provider === 'azure' ? azureDeployment : undefined,
        // model applies to OpenAI and Anthropic; Azure uses azure_deployment instead.
        model: provider === 'azure' ? undefined : model,
      };

      if (connection) {
        await aiService.updateConnection(connection.id, configPayload);
        toast.success('AI configuration updated', { id: toastId });
      } else {
        await aiService.createConnection(configPayload);
        toast.success('AI configured successfully', { id: toastId });
      }

      setIsConfiguring(false);
      setApiKey('');
      setAzureDeployment('');
      onUpdate();
    } catch (error) {
      console.error('Failed to save AI configuration:', error);
      toast.error('Failed to save AI configuration', { id: toastId });
    }
  };

  const handleDisconnect = async () => {
    if (!connection) return;

    const confirmed = await confirm({
      title: 'Remove AI Configuration?',
      message: 'This will remove your AI API credentials. You will need to reconfigure to use AI features.',
      confirmText: 'Remove',
      cancelText: 'Cancel',
      variant: 'danger',
    });

    if (!confirmed) return;

    const toastId = toast.loading('Removing AI configuration...');

    try {
      await aiService.deleteConnection(connection.id);
      toast.success('AI configuration removed', { id: toastId });
      onUpdate();
    } catch (error) {
      console.error('Failed to remove AI configuration:', error);
      toast.error('Failed to remove AI configuration', { id: toastId });
    }
  };

  const handleTest = async () => {
    if (!connection) return;

    setTesting(true);
    const toastId = toast.loading('Testing AI connection...');

    try {
      // Test with a simple prompt
      const testSummary = await aiService.generateExecutiveSummary({
        title: 'Test Contract',
        clientName: 'Test Client',
      });

      if (testSummary && testSummary.length > 0) {
        toast.success('AI connection test successful!', { id: toastId });
      } else {
        throw new Error('Received empty response');
      }
    } catch (error) {
      console.error('AI connection test failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Connection test failed';
      toast.error(`Test failed: ${errorMessage}`, { id: toastId });
    } finally {
      setTesting(false);
    }
  };

  const getProviderDisplay = () => {
    if (!connection) return null;
    const provider = connection.config?.provider;
    if (provider === 'azure') return 'Azure OpenAI';
    if (provider === 'anthropic') {
      const model = connection.config?.model || 'claude-opus-4-7';
      return `Anthropic (${model})`;
    }
    if (provider === 'openai') {
      const model = connection.config?.model || 'gpt-4o-mini';
      return `OpenAI (${model})`;
    }
    return 'AI';
  };

  return (
    <>
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <SparklesIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">AI Provider</h3>
                <p className="text-sm text-gray-500">
                  {connection ? getProviderDisplay() : 'Not configured'}
                </p>
              </div>
            </div>
            {connection && connection.status === 'active' && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <CheckCircleIcon className="w-4 h-4 mr-1" />
                Configured
              </span>
            )}
          </div>

          {isConfiguring ? (
            <div className="space-y-4 mt-6">
              {/* Provider Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  AI Provider
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {([
                    { id: 'openai', label: 'OpenAI' },
                    { id: 'azure', label: 'Azure OpenAI' },
                    { id: 'anthropic', label: 'Anthropic' },
                  ] as { id: AIProvider; label: string }[]).map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => handleProviderChange(p.id)}
                      className={`px-4 py-3 border-2 rounded-lg text-sm font-medium transition-colors ${
                        provider === p.id
                          ? 'border-purple-600 bg-purple-50 text-purple-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Azure Endpoint (only for Azure) */}
              {provider === 'azure' && (
                <>
                  <div>
                    <label htmlFor="azure-endpoint" className="block text-sm font-medium text-gray-700 mb-1">
                      Azure OpenAI Endpoint
                    </label>
                    <input
                      type="url"
                      id="azure-endpoint"
                      value={azureEndpoint}
                      onChange={(e) => setAzureEndpoint(e.target.value)}
                      placeholder="https://your-resource.cognitiveservices.azure.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Base endpoint only (do not include /openai/deployments/...)
                    </p>
                  </div>

                  <div>
                    <label htmlFor="azure-deployment" className="block text-sm font-medium text-gray-700 mb-1">
                      Deployment Name
                    </label>
                    <input
                      type="text"
                      id="azure-deployment"
                      value={azureDeployment}
                      onChange={(e) => setAzureDeployment(e.target.value)}
                      placeholder="gpt-4"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      The deployment name you created in Azure Portal (e.g., gpt-4, gpt-35-turbo)
                    </p>
                  </div>
                </>
              )}

              {/* Model selection — OpenAI and Anthropic use a model list;
                  Azure picks the model implicitly via deployment name. */}
              {provider !== 'azure' && (
                <div>
                  <label htmlFor="model" className="block text-sm font-medium text-gray-700 mb-1">
                    Model
                  </label>
                  <select
                    id="model"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    {(provider === 'anthropic' ? ANTHROPIC_MODELS : OPENAI_MODELS).map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Choose the model for AI operations
                  </p>
                </div>
              )}

              {/* API Key */}
              <div>
                <label htmlFor="api-key" className="block text-sm font-medium text-gray-700 mb-1">
                  API Key
                </label>
                <input
                  type="password"
                  id="api-key"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={
                    provider === 'azure' ? 'Azure OpenAI API key'
                    : provider === 'anthropic' ? 'Anthropic API key (sk-ant-...)'
                    : 'OpenAI API key (sk-...)'
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {provider === 'azure' ? 'Found in Azure Portal under Keys and Endpoint'
                   : provider === 'anthropic' ? 'Found at console.anthropic.com → Settings → API Keys'
                   : 'Found at platform.openai.com/api-keys'}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
                >
                  Save Configuration
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-6">
                Configure AI to enable intelligent features like executive summary generation and document analysis.
              </p>

              <div className="flex gap-3">
                {!connection ? (
                  <button
                    onClick={handleConfigure}
                    className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    Configure AI
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleTest}
                      disabled={testing}
                      className="inline-flex items-center px-3 py-2 border border-purple-300 text-sm font-medium rounded-md text-purple-700 bg-white hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CheckCircleIcon className={`w-4 h-4 mr-2 ${testing ? 'animate-spin' : ''}`} />
                      {testing ? 'Testing...' : 'Test Connection'}
                    </button>

                    <button
                      onClick={handleConfigure}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    >
                      Update Configuration
                    </button>

                    <button
                      onClick={handleDisconnect}
                      className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <TrashIcon className="w-4 h-4 mr-2" />
                      Remove
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <ConfirmDialog />
    </>
  );
}

